import { useEffect, useRef, useState, type ReactNode } from 'react';
import { formatServerClock } from '../shell/serverTime';
import { gridLevels, tickPaths, type Tick } from './tickModel';

/** Room reserved for the right-hand price axis and its bid/ask tags, and for the time axis along the bottom. */
const AXIS_W = 58;
const TIME_H = 16;

/**
 * The tick chart: bid and ask as they arrived, with a price axis and live bid/ask tags on the right and a time axis along
 * the bottom, as MT5 draws its own. The caller keeps the ticks (and decides which symbol); this only draws them.
 */
export function TickChartView({
  symbol,
  digits,
  ticks,
  note,
}: {
  symbol: string;
  digits: number;
  ticks: readonly Tick[];
  /** A line under the chart, explaining where the ticks come from. */
  note?: ReactNode;
}) {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  // The pointer over the chart, for the crosshair: where it is, in the drawing's own coordinates.
  const [pointer, setPointer] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) =>
      setSize({ width: entry!.contentRect.width, height: entry!.contentRect.height }),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const list = ticks;
  const last = list[list.length - 1];
  const plotW = Math.max(0, size.width - AXIS_W);
  const plotH = Math.max(0, size.height - TIME_H);
  const paths = tickPaths(list, plotW, plotH);
  const levels = paths ? gridLevels(paths.lo, paths.hi) : [];
  const xAt = (i: number) => (list.length <= 1 ? plotW : (i / (list.length - 1)) * plotW);
  // A handful of evenly spaced time labels along the bottom, like MT5's own time axis.
  const timeTickCount = Math.min(list.length, Math.max(2, Math.floor(plotW / 90)));
  const timeTicks =
    list.length > 1
      ? Array.from({ length: timeTickCount }, (_, i) =>
          Math.round((i / (timeTickCount - 1)) * (list.length - 1)),
        )
      : [];

  return (
    <section className="flex h-full flex-col bg-panel" aria-label="Tick chart">
      <header className="flex flex-wrap items-center gap-3 border-b border-line px-2 py-1 text-xs">
        <span className="font-semibold">{symbol} · ticks</span>
        {last && (
          <>
            <span className="num text-down">Bid {last.bid.toFixed(digits)}</span>
            <span className="num text-up">Ask {last.ask.toFixed(digits)}</span>
            <span className="num text-muted">{formatServerClock(last.t, 0)}</span>
          </>
        )}
        <span className="num text-muted" data-testid="tick-count">
          {list.length} updates
        </span>
      </header>
      <div ref={box} className="relative min-h-0 flex-1">
        {paths ? (
          <svg
            width={size.width}
            height={size.height}
            role="img"
            aria-label={`Bid and ask of ${symbol}`}
            onPointerMove={(e) => {
              const box = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - box.left;
              const y = e.clientY - box.top;
              setPointer(x >= 0 && x <= plotW && y >= 0 && y <= plotH ? { x, y } : null);
            }}
            onPointerLeave={() => setPointer(null)}
          >
            {/* Horizontal grid, with the price it stands for on the right-hand axis. */}
            {levels.map((level) => {
              const y = paths.y(level);
              return (
                <g key={level}>
                  <line
                    x1={0}
                    x2={plotW}
                    y1={y}
                    y2={y}
                    stroke="var(--border)"
                    strokeWidth={1}
                    strokeDasharray="2 3"
                  />
                  <text x={plotW + 4} y={y + 3} className="fill-current text-[9px] text-muted">
                    {level.toFixed(digits)}
                  </text>
                </g>
              );
            })}
            {/* Vertical grid at the same points the time axis labels. */}
            {timeTicks.map((i) => (
              <line
                key={`v${i}`}
                x1={xAt(i)}
                x2={xAt(i)}
                y1={0}
                y2={plotH}
                stroke="var(--border)"
                strokeWidth={1}
                strokeDasharray="2 3"
              />
            ))}
            <path d={paths.ask} fill="none" stroke="var(--up)" strokeWidth={1.5} />
            <path d={paths.bid} fill="none" stroke="var(--down)" strokeWidth={1.5} />
            {/* The time axis along the bottom. */}
            {timeTicks.map((i) => (
              <text
                key={`t${i}`}
                x={Math.min(Math.max(xAt(i), 18), plotW - 18)}
                y={plotH + 12}
                textAnchor="middle"
                className="fill-current text-[9px] text-muted"
              >
                {formatServerClock(list[i]!.t, 0)}
              </text>
            ))}
            {/* The crosshair: the price under the pointer on the right, the time below, the bid and ask of that moment above. */}
            {pointer &&
              (() => {
                const index = list.length <= 1 ? 0 : Math.round((pointer.x / plotW) * (list.length - 1));
                const tick = list[Math.max(0, Math.min(list.length - 1, index))]!;
                const at = paths.lo + ((plotH - pointer.y) / plotH) * (paths.hi - paths.lo);
                const tickX = xAt(index);
                return (
                  <g pointerEvents="none">
                    <line
                      x1={tickX}
                      x2={tickX}
                      y1={0}
                      y2={plotH}
                      stroke="var(--text)"
                      strokeWidth={0.6}
                      strokeDasharray="3 3"
                    />
                    <line
                      x1={0}
                      x2={plotW}
                      y1={pointer.y}
                      y2={pointer.y}
                      stroke="var(--text)"
                      strokeWidth={0.6}
                      strokeDasharray="3 3"
                    />
                    <g transform={`translate(${plotW}, ${pointer.y})`}>
                      <rect x={0} y={-7} width={AXIS_W} height={14} fill="#555" />
                      <text x={4} y={4} className="fill-white text-[9px]">
                        {at.toFixed(digits)}
                      </text>
                    </g>
                    <g transform={`translate(${Math.min(Math.max(tickX, 52), plotW - 52)}, ${plotH + 1})`}>
                      <rect x={-52} y={0} width={104} height={TIME_H - 1} fill="#555" />
                      <text x={0} y={11} textAnchor="middle" className="fill-white text-[9px]">
                        {formatServerClock(tick.t, 0)}
                      </text>
                    </g>
                    <g transform={`translate(${Math.min(Math.max(tickX, 60), plotW - 60)}, 4)`}>
                      <rect x={-60} y={0} width={120} height={14} fill="#555" opacity={0.85} />
                      <text x={0} y={10} textAnchor="middle" className="fill-white text-[9px]">
                        {tick.bid.toFixed(digits)} / {tick.ask.toFixed(digits)}
                      </text>
                    </g>
                  </g>
                );
              })()}
            {/* The live bid and ask, pinned to the right-hand axis at their price. */}
            {last && (
              <>
                <g transform={`translate(${plotW}, ${paths.y(last.bid)})`}>
                  <rect x={0} y={-7} width={AXIS_W} height={14} fill="var(--down)" />
                  <text x={4} y={4} className="fill-white text-[9px] font-semibold">
                    {last.bid.toFixed(digits)}
                  </text>
                </g>
                <g transform={`translate(${plotW}, ${paths.y(last.ask)})`}>
                  <rect x={0} y={-7} width={AXIS_W} height={14} fill="var(--up)" />
                  <text x={4} y={4} className="fill-white text-[9px] font-semibold">
                    {last.ask.toFixed(digits)}
                  </text>
                </g>
              </>
            )}
          </svg>
        ) : (
          <p className="p-3 text-sm text-muted">Waiting for prices of {symbol}…</p>
        )}
      </div>
      {note && <p className="border-t border-line px-2 py-1 text-xs text-muted">{note}</p>}
    </section>
  );
}
